import React, { useEffect, useState } from "react";
import { Checkbox } from "@remo-co/ui-core/src/components/Checkbox";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogContentText } from "@remo-co/ui-core/src/components/DialogContentText";
import { FormControlLabel } from "@remo-co/ui-core/src/components/FormControlLabel";
import { TextField } from "@remo-co/ui-core/src/components/TextField";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useSelector } from "react-redux";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { getEmbeddedVideoURL, parseVideo } from "helpers/videoHelper";
import { useBroadcastVideoActions } from "modules/broadcast/hooks/useBroadcastVideoActions";
import { useI18n } from "i18n";
import { searchYoutubeVideo } from "modules/youtube";
import logger from "logging/logger";
import {
  addDialogNotification,
  closeDialogNotificationsByType,
} from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { CheckedIcon } from "@remo-co/ui-core/src/icons/Checked";
import { DialogType } from "../../../dialogNotification/constants";
import {
  selectIsInBroadcast,
  selectIsVideoControlsEnabled,
} from "../../redux/selectors";
import { useStyles } from "./useStyles";
import { styles } from "./styles";

interface IBroadcastVideoInputDialogProps {
  url?: string;
  onClose: () => void;
  handleIsVideoControlsEnabledChange: (isEnabled: boolean) => void;
}

export const BroadcastVideoInputDialog = ({
  url = "",
  onClose,
  handleIsVideoControlsEnabledChange,
}: IBroadcastVideoInputDialogProps): null => {
  const dispatch = useAppDispatch();
  const { t } = useI18n(["common", "event", "manageEvent", "errorPage"]);
  const { addErrorNotification } = useNotificationActions();
  const [videoURL, setVideoURL] = useState<string>(url);
  const [videoURLText, setVideoURLText] = useState<string>(url);
  const { updateVideoURL } = useBroadcastVideoActions();
  const isVideoControlsEnabled = useSelector(selectIsVideoControlsEnabled);
  const isInBroadcast = useSelector(selectIsInBroadcast);
  const classes = useStyles({ isInBroadcast: Boolean(isInBroadcast) });

  useEffect(() => {
    dispatch(
      addDialogNotification({
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        content: renderForm(),
        hideCloseButton: true,
        type: DialogType.ROOM_NAME,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        onConfirm: handleSubmit,
        confirmTestId: "video-input-confirm",
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        onDismiss: handleCancel,
        dismissText: t("button.cancel"),
        confirmText: t("event:play.now"),
        confirmDisabled: videoURL.startsWith("http://"),
        contentClassName: classes.dialogContent,
        dismissBtnClassName: classes.dismissBtn,
        confirmBtnClassName: classes.confirmBtn,
        actionStyles: styles.actionBtn,
      }),
    );

    return () => {
      dispatch(closeDialogNotificationsByType(DialogType.ROOM_NAME));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoURL, isVideoControlsEnabled]);

  useEffect(() => {
    setVideoURL(videoURLText);
  }, [videoURLText, isVideoControlsEnabled]);
  const handleURLChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value } = event.target;

    setVideoURLText(value);
  };

  const appendIsLiveIfLiveYoutubeVideo = async (
    ytVideoUrl: string,
    videoObj: ReturnType<typeof parseVideo>,
  ) => {
    try {
      const youtubeVideoData = await searchYoutubeVideo(videoObj.id);

      if (youtubeVideoData?.snippet?.liveBroadcastContent === "live") {
        return `${ytVideoUrl}&isLive=1`;
      }
    } catch (error) {
      addErrorNotification({
        position: "tr",
        message: t("server:unable.to.determine.live.video"),
      });

      logger.error(error as string);
    }

    return ytVideoUrl;
  };

  const displayInvalidVideoUrlError = () => {
    addErrorNotification({
      message: t("server:invalid.video.url.input"),
      position: "tc",
      autoDismiss: 5,
    });
  };

  const handleSubmit = async () => {
    if (!videoURL) {
      displayInvalidVideoUrlError();
      onClose();

      return;
    }
    const embeddedURL = getEmbeddedVideoURL(videoURL);

    if (!embeddedURL) {
      displayInvalidVideoUrlError();
      onClose();

      return;
    }

    const videoObj = parseVideo(videoURL);

    if (videoObj?.type === "youtube") {
      const resultingUrl = await appendIsLiveIfLiveYoutubeVideo(
        videoURL,
        videoObj,
      );

      updateVideoURL(resultingUrl);
    } else {
      updateVideoURL(videoURL);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const onVideoControlsEnabledChange = (
    e: React.ChangeEvent,
    isChecked: boolean,
  ) => {
    handleIsVideoControlsEnabledChange(isChecked);
  };

  const renderForm = () => (
    <DialogContent>
      <DialogContentText style={{ textAlign: "center" }}>
        <Typography variant="h3" className={classes.title}>
          {t("manageEvent:broadcast.video.url")}
        </Typography>
        <TextField
          placeholder={t("manageEvent:paste.url.here")}
          fullWidth
          value={videoURL}
          onChange={handleURLChange}
          inputProps={{
            className: classes.input,
            "data-testid": "video-url-input",
          }}
          size="small"
          error={videoURL.startsWith("http://")}
          helperText={
            videoURL.startsWith("http://") ? t("incorrect.url.message") : ""
          }
        />
        <Box sx={styles.dialogTextSpaceBtwn}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={onVideoControlsEnabledChange}
                data-testid="video-controls-checkbox"
                name="videoControls"
                checked={!!isVideoControlsEnabled}
                icon={<Typography sx={styles.checkbox} />}
                checkedIcon={
                  <Typography sx={styles.checkboxIcon}>
                    <CheckedIcon />
                  </Typography>
                }
              />
            }
            label={t("event:enable.video.controls.for.participants")}
          />
        </Box>
      </DialogContentText>
    </DialogContent>
  );

  return null;
};
